import React, { useCallback, useEffect, useState } from 'react';
import { C, S } from '../../../../utils/styles';
import Icon from '../../../Icon';
import { useToast } from '../../../Toast';
import { listNewsletters, deleteNewsletter, getNewsletter } from './newsletterApi';
import NewsletterEditor from './NewsletterEditor';

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) { return '—'; }
}

function statusPillStyle(status) {
  if (status === 'sent')  return { background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}` };
  if (status === 'draft') return { background: C.accentBg,   color: C.accent, border: `1px solid ${C.accent}33` };
  return { background: C.bg, color: C.textMid, border: `1px solid ${C.border}` };
}

export default function NewsletterListPanel() {
  const showToast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);  // when set → show editor
  const [creatingNew, setCreatingNew] = useState(false);
  const [viewSent, setViewSent] = useState(null);    // sent newsletter id → opens detail drawer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNewsletters();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message || 'Failed to load newsletters', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (n) => {
    if (!window.confirm(`Delete draft "${n.issueTitle}"? This can't be undone.`)) return;
    try {
      await deleteNewsletter(n.id);
      showToast('Draft deleted', 'success');
      load();
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  };

  if (creatingNew || editingId) {
    return (
      <NewsletterEditor
        newsletterId={editingId}
        onBack={() => { setEditingId(null); setCreatingNew(false); load(); }}
        onChanged={() => load()}
      />
    );
  }

  const drafts = items.filter((i) => i.status === 'draft');
  const sent = items.filter((i) => i.status === 'sent');

  return (
    <div data-testid="newsletter-list" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={S.heading}>Monthly Newsletter</div>
            <p style={{ margin: 0, fontSize: 13, color: C.textMid, maxWidth: 720, lineHeight: 1.6 }}>
              Build a beautifully designed HealthCues newsletter once a month and deliver it to your entire community —
              Account Managers, Employer contacts, and Employees — all in one send. Every sent newsletter is locked and
              archived forever for compliance and your own record.
            </p>
          </div>
          <button
            type="button"
            data-testid="newsletter-create-btn"
            onClick={() => setCreatingNew(true)}
            style={S.buttonPrimary}
          >
            <Icon name="add-outline" style={{ fontSize: 16, marginRight: 4, verticalAlign: '-2px' }} />
            New Newsletter
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <StatPill label="Drafts" value={drafts.length} color={C.accent} />
          <StatPill label="Sent" value={sent.length} color={C.green} />
          <StatPill label="Total" value={items.length} color={C.text} />
        </div>
      </div>

      {/* Drafts table */}
      <div style={S.card}>
        <div style={{ ...S.heading, marginBottom: 12 }}>Drafts in progress ({drafts.length})</div>
        <Table
          loading={loading}
          rows={drafts}
          emptyLabel="No drafts. Start a new newsletter to begin."
          onOpen={(n) => setEditingId(n.id)}
          onDelete={handleDelete}
          canDelete={true}
          canView={false}
        />
      </div>

      {/* Sent archive */}
      <div style={S.card}>
        <div style={{ ...S.heading, marginBottom: 12 }}>Sent archive ({sent.length})</div>
        <Table
          loading={loading}
          rows={sent}
          emptyLabel="No newsletters sent yet."
          onOpen={(n) => setViewSent(n.id)}
          canDelete={false}
          canView={true}
        />
      </div>

      {viewSent && (
        <SentNewsletterDrawer id={viewSent} onClose={() => setViewSent(null)} />
      )}
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div style={{ padding: '8px 14px', borderRadius: 10, background: C.bg, border: `1px solid ${C.border}`, minWidth: 88 }}>
      <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}

function Table({ loading, rows, emptyLabel, onOpen, onDelete, canDelete, canView }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
        <thead>
          <tr>
            <th style={S.th}>Issue</th>
            <th style={S.th}>Month</th>
            <th style={S.th}>Status</th>
            <th style={S.th}>Stories</th>
            <th style={S.th}>Recipients</th>
            <th style={S.th}>Last Updated</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={7}>Loading…</td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={7}>{emptyLabel}</td></tr>
          )}
          {!loading && rows.map((n) => (
            <tr key={n.id} data-testid={`newsletter-row-${n.id}`}>
              <td style={S.td}>
                <button
                  type="button"
                  data-testid={`newsletter-open-${n.id}`}
                  onClick={() => onOpen(n)}
                  style={{ background: 'transparent', border: 'none', padding: 0, color: C.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'left', fontFamily: "'Inter', sans-serif" }}
                >
                  {n.issueTitle}
                </button>
                {n.tagline ? (
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
                    {n.tagline.length > 90 ? `${n.tagline.slice(0, 90)}…` : n.tagline}
                  </div>
                ) : null}
              </td>
              <td style={S.td}>{n.issueMonth || '—'}</td>
              <td style={S.td}>
                <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', ...statusPillStyle(n.status) }}>
                  {n.status}
                </span>
              </td>
              <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{(n.stories || []).length}</td>
              <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{n.recipientCount ?? '—'}</td>
              <td style={S.td}>{formatDate(n.status === 'sent' ? n.sentAt : n.updatedAt)}</td>
              <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button
                  type="button"
                  data-testid={`newsletter-${canView ? 'view' : 'edit'}-${n.id}`}
                  onClick={() => onOpen(n)}
                  style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}
                >
                  {canView ? 'View' : 'Edit'}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    data-testid={`newsletter-delete-${n.id}`}
                    onClick={() => onDelete(n)}
                    style={{ ...S.buttonDanger, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SentNewsletterDrawer({ id, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const showToast = useToast();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getNewsletter(id)
      .then((d) => { if (mounted) setDetail(d); })
      .catch((err) => showToast(err.message || 'Failed to load', 'error'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id, showToast]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(2px)', zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div data-testid="newsletter-sent-drawer" style={{ width: '100%', maxWidth: 760, height: '100%', background: '#fff', boxShadow: '-8px 0 24px rgba(0,0,0,0.10)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sent newsletter</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {loading ? 'Loading…' : `${detail?.issueTitle || '—'}${detail?.issueMonth ? ' · ' + detail.issueMonth : ''}`}
            </div>
          </div>
          <button type="button" data-testid="newsletter-drawer-close" onClick={onClose} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>Close</button>
        </div>

        {!loading && detail && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, padding: '14px 18px', borderBottom: `1px solid ${C.borderLight}` }}>
            <Meta label="Sent" value={detail.sentAt ? new Date(detail.sentAt).toLocaleString() : '—'} />
            <Meta label="Recipients" value={detail.recipientCount ?? '—'} />
            <Meta label="Sender" value={detail.sender?.name ? `${detail.sender.name} <${detail.sender.email || ''}>` : '—'} />
            <Meta label="Stories" value={(detail.stories || []).length} />
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: 18, background: '#f1f5f9' }}>
          {detail?.renderedHtml ? (
            <iframe
              title="Sent newsletter"
              data-testid="newsletter-sent-iframe"
              srcDoc={detail.renderedHtml}
              style={{ width: '100%', height: '100%', minHeight: 600, border: 'none', background: '#f1f5f9' }}
            />
          ) : (
            <div style={{ color: C.textLight, fontStyle: 'italic', fontSize: 13 }}>No rendered HTML stored.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 13, color: C.text, fontWeight: 500, marginTop: 2 }}>{value}</div>
    </div>
  );
}
